import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PhotoLibraryRoundedIcon from '@mui/icons-material/PhotoLibraryRounded';
import { providersService } from '../../shared/services/providers.service';
import type { ProviderStatus } from '../../shared/types';
import { useAuth } from '../../shared/hooks/useAuth';
import { useApiError } from '../../shared/errors/useApiError';
import { useNotification } from '../../shared/notifications/NotificationProvider';
import { ConfirmDeleteByEmailDialog } from './ConfirmDeleteByEmailDialog';

const STATUS_OPTIONS: ProviderStatus[] = ['PENDING', 'APPROVED', 'REJECTED'];

export function AdminProvidersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { notify } = useNotification();
  const { translate } = useApiError();
  const { hasRole } = useAuth();
  const canDelete = hasRole('SUPER_ADMIN');
  const [rejecting, setRejecting] = useState<{ id: string } | null>(null);
  const [reason, setReason] = useState('');
  const [deleting, setDeleting] = useState<{ id: string; businessName: string } | null>(null);
  const [statusFilter, setStatusFilter] = useState<ProviderStatus>('PENDING');

  const list = useQuery({
    queryKey: ['admin', 'providers', statusFilter],
    queryFn: () => providersService.adminList(statusFilter, 0, 50),
  });

  const approve = useMutation({
    mutationFn: (id: string) => providersService.adminApprove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'providers'] });
      notify.success(t('provider.statusApproved'));
    },
    onError: (err) => notify.error(translate(err)),
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      providersService.adminReject(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'providers'] });
      notify.success(t('provider.statusRejected'));
      setRejecting(null);
      setReason('');
    },
    onError: (err) => notify.error(translate(err)),
  });

  // Deletion is now gated by an emailed 6-digit confirmation code — the
  // dialog handles the full request/confirm flow itself; we just invalidate
  // caches once it reports success.
  const onDeleteSuccess = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'providers'] });
    qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    notify.success(t('adminUsers.deleted'));
    setDeleting(null);
  };

  if (list.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const items = list.data?.data ?? [];

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {t('admin.providersTitle')}
        </Typography>
        <TextField
          select
          size="small"
          label={t('admin.statusFilter', { defaultValue: 'Statut' })}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as ProviderStatus)}
          sx={{ minWidth: 180 }}
        >
          {STATUS_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>
              {t(`provider.status${s.charAt(0) + s.slice(1).toLowerCase()}`, {
                defaultValue: s,
              })}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {items.length === 0 ? (
        <Typography color="text.secondary">{t('admin.noProviders')}</Typography>
      ) : (
        <Stack spacing={2}>
          {items.map((p) => (
            <Paper key={p.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ sm: 'center' }}
              >
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {p.businessName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {p.city.name}, {p.city.country}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate(`/admin/providers/${p.id}`)}
                  >
                    {t('search.viewProfile')}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PhotoLibraryRoundedIcon />}
                    onClick={() => navigate(`/admin/providers/${p.id}/portfolio`)}
                  >
                    {t('admin.portfolioCta', { defaultValue: 'Portfolio' })}
                  </Button>
                  {statusFilter !== 'APPROVED' && (
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => approve.mutate(p.id)}
                      disabled={approve.isPending}
                    >
                      {t('admin.approve')}
                    </Button>
                  )}
                  {statusFilter !== 'REJECTED' && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => setRejecting({ id: p.id })}
                    >
                      {t('admin.reject')}
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={() => setDeleting({ id: p.id, businessName: p.businessName })}
                    >
                      {t('common.delete')}
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      {deleting && (
        <ConfirmDeleteByEmailDialog
          open={Boolean(deleting)}
          onClose={() => setDeleting(null)}
          onSuccess={onDeleteSuccess}
          title={t('adminUsers.confirmTitle')}
          targetLabel={deleting.businessName}
          requestCode={() => providersService.adminRequestDelete(deleting.id)}
          confirm={(code) => providersService.adminDelete(deleting.id, code)}
        />
      )}

      <Dialog open={Boolean(rejecting)} onClose={() => setRejecting(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t('admin.reject')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('admin.rejectReason')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            minRows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejecting(null)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            disabled={!reason || reject.isPending}
            onClick={() => rejecting && reject.mutate({ id: rejecting.id, reason })}
          >
            {t('admin.reject')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
